import {
  getReturnsCoverageExclusions,
  itemValidForFinalSaleReturns,
  itemValidForProtection,
  itemValidForReturns,
  listsHaveMatchingElement,
} from "@redotech/redo-model/coverage-calculator/check-product";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  Collection,
  ExtensionShopifyClient,
  getTags,
  ShopifyProduct,
} from "@redotech/shopify-client/ajax";
import {
  getCollections,
  ShopifyExtensionClient,
} from "../client/shopify-extension-client";
import { log } from "../monitoring/log";

export async function itemValidForReturnsFrontend({
  autocheck,
  item,
  redoShopifyExtensionClient,
  shopifyClient,
  signal,
  invalidReturnProductTags,
  invalidProtectionProductTags,
  shopifyProduct,
}: {
  autocheck: WidgetAutocheck;
  item: {
    product_id: string | number;
    handle: string;
    properties?: Record<string, string> | null;
  };
  redoShopifyExtensionClient: ShopifyExtensionClient;
  shopifyClient: ExtensionShopifyClient;
  signal?: AbortSignal;
  invalidReturnProductTags: string[];
  invalidProtectionProductTags?: string[];
  shopifyProduct?: ShopifyProduct;
}): Promise<{
  returnsValid: boolean;
  protectionValid: boolean;
  finalSaleReturnsValid: boolean;
}> {
  const returnStorageValue = await getLocalStorageWithExpiry(
    `redo-return-valid-excl-props-${item.product_id}`,
  );
  const protectionStorageValue = await getLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
  );
  const finalSaleStorageValue = await getLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
  );

  const properties: string[] = item.properties
    ? Object.keys(item.properties).reduce((acc, key) => {
        const value = item.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  const hasValidReturnProductProperties = !listsHaveMatchingElement(
    properties,
    autocheck.coverageExcludeProperties,
  );

  if (
    returnStorageValue !== null &&
    protectionStorageValue !== null &&
    finalSaleStorageValue !== null
  ) {
    return {
      returnsValid:
        returnStorageValue === "true" && hasValidReturnProductProperties,
      protectionValid: protectionStorageValue === "true",
      finalSaleReturnsValid: finalSaleStorageValue === "true",
    };
  }

  let productTags: string[] = [];
  let collections: Collection[] = [];

  // TODO: support tags & collections for SFCC
  if (globalThis.redoStorefront?.type !== "commercecloud") {
    let product: ShopifyProduct | undefined = undefined;
    try {
      product =
        shopifyProduct ?? (await shopifyClient.productGet(item.handle, signal));
      productTags = getTags(product);
    } catch (e) {
      // For BigCommerce, if a product is set to is_visible: false (e.g. a GWP product which the merchant
      // doesn't want to be shown, but is purchaseable), we will not be able to retrieve the product via
      // storefront API. However, that means invisible products will usually always be treated as eligible.
      // If we need to support tag-filtering for invisible products, we will need to create an endpoint on
      // our Embed API to retrieve the product tags.
      if (globalThis.redoStorefront?.type === "bigcommerce") {
        log(
          `Error getting product tags for ${item.handle} - product is possibly not visible. Assuming no tags.`,
          e,
          item.handle,
        );
      } else {
        throw e;
      }
    }
    if (
      autocheck.coverageExcludeCollections.length ||
      autocheck.finalSaleReturns.coverageValidCollections.length
    ) {
      if (globalThis.redoStorefront?.type === "bigcommerce") {
        collections =
          (await shopifyClient.getCollections(product?.id?.toString())) ?? [];
      } else {
        collections =
          (await getCollections(redoShopifyExtensionClient, {
            signal,
            productId: String(item.product_id),
          })) ?? [];
      }
    }
  }

  const coverageExclusions = getReturnsCoverageExclusions({
    returnsCoverage: {
      coverageExcludeProperties: autocheck.coverageExcludeProperties,
      coverageExcludeCollections: autocheck.coverageExcludeCollections,
      invalidProductTags: invalidReturnProductTags,
    },
    finalSaleReturnsCoverage: {
      coverageEnabled: autocheck.finalSaleReturns.coverageEnabled,
      validProductTags: autocheck.finalSaleReturns.coverageValidTags,
      validCollectionTitles:
        autocheck.finalSaleReturns.coverageValidCollectionTitles ?? [],
    },
  });

  const hasValidReturnProductIgnoringProperties =
    itemValidForReturns({
      ...coverageExclusions,
      itemInfo: {
        properties: [], // We are going to cache this result in local storage, so don't take into account the properties since they change with each item added
        productTags,
        collections: collections.map((c) => c.title),
      },
    }) && autocheck.coverageEnabled;

  const hasValidProtectionProduct =
    itemValidForProtection({
      invalidProductTags: invalidProtectionProductTags || [],
      itemInfo: {
        properties,
        productTags,
        collections: collections.map((c) => c.title),
      },
    }) && autocheck.packageProtection.coverageEnabled;

  const hasValidFinalSaleProduct =
    itemValidForFinalSaleReturns({
      validProductTags: autocheck.finalSaleReturns.coverageValidTags,
      validCollections: autocheck.finalSaleReturns.coverageValidCollections,
      itemInfo: { productTags, collections: collections.map((c) => c.handle) },
    }) && autocheck.finalSaleReturns.coverageEnabled;

  setLocalStorageWithExpiry(
    `redo-return-valid-excl-props-${item.product_id}`,
    String(hasValidReturnProductIgnoringProperties),
  );

  setLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
    String(hasValidProtectionProduct),
  );

  setLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
    String(hasValidFinalSaleProduct),
  );

  return {
    returnsValid:
      hasValidReturnProductIgnoringProperties &&
      hasValidReturnProductProperties,
    protectionValid: hasValidProtectionProduct,
    finalSaleReturnsValid: hasValidFinalSaleProduct,
  };
}
